import { Link } from 'react-router-dom';
import { ChevronRight, CircleAlert } from 'lucide-react';
import { TableCard } from './TableCard';
import { UserText } from './UserText';

export interface NeedsAttentionItem {
  key: string;
  /** Bold, and user text (a person's or family's name), so it goes through `UserText`. */
  label: string;
  /** Grey detail after the label. */
  detail: string;
  href: string;
}

/**
 * The "Needs attention" list, shared by the family Dashboard and the firm Overview
 * (steward-app#1890): a bronze-edged card, one row per thing, each row a bronze icon, a bold
 * name, grey detail and a chevron. Extracted from the Dashboard's inline markup so the two pages
 * cannot drift apart. The classes are the Dashboard's except three changes that come with the
 * row becoming a `Link`: `no-underline` and a `focus-visible` ring are added, and
 * `cursor-pointer` is dropped because an anchor already has one.
 *
 * Renders nothing for an empty list (the caller decides the section is absent, not "nothing to
 * see"), and takes the heading with it so the heading never sits over an empty card. `more` is
 * the number of items the caller left out, shown as "+ N more".
 *
 * Each row is a real link, so the whole row is one keyboard stop and opens in a new tab on
 * request; the Dashboard's rows used to be `div`s with an `onClick`.
 */
export function NeedsAttentionList({ items, more = 0 }: { items: readonly NeedsAttentionItem[]; more?: number }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-10">
      <div className="font-serif text-base font-normal text-navy mb-4">Needs attention</div>
      <TableCard className="border-l-[3px] border-l-bronze shadow-panel">
        {items.map(item => (
          <Link
            key={item.key}
            to={item.href}
            className="flex items-center justify-between px-5 py-3.5 border-b border-line/60 last:border-b-0 no-underline hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bronze-strong"
          >
            <div className="flex items-center gap-3 min-w-0">
              <CircleAlert size={15} strokeWidth={1.75} aria-hidden className="text-bronze shrink-0" />
              <span className="font-sans font-semibold text-[13px] text-navy truncate"><UserText>{item.label}</UserText></span>
              <span className="text-[12px] text-slate font-sans shrink-0">{item.detail}</span>
            </div>
            <ChevronRight size={14} strokeWidth={1.75} aria-hidden className="text-slate shrink-0" />
          </Link>
        ))}
        {more > 0 && (
          <div className="px-5 py-2.5 text-[11px] text-slate font-sans font-semibold uppercase tracking-[0.08em]">
            + {more} more
          </div>
        )}
      </TableCard>
    </div>
  );
}
